import React, { useState } from 'react';
import {
  FileText, Trash2, Upload, Video, Link2, ExternalLink, Plus,
} from 'lucide-react';
import { fetchJSON, API_BASE_URL } from '../../utils/api';
import { fmtBytes } from '../workflow/constants';
import { formatDateTime } from '../../utils/dateFormat';
import { FilePicker, inputCls } from '../shared/FormKit';
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';

/**
 * Course materials / recordings on a training session (docs/modules/ld.md
 * §3.6) — the list, and (when `canManage`) upload, add a link, remove.
 *
 * One component, two kinds:
 *   kind="material"   slides and handouts — documents, 50 MB each
 *   kind="recording"  the course video — mp4 / mov / webm up to 500 MB, or a
 *                     link to wherever a bigger file lives
 * Either kind may hold an external link (a deck on a shared drive, a video on
 * a streaming host): the row then opens the link instead of a stored file.
 *
 * Used by the session page (participants read, trainers manage), the
 * Training Records View modal (L&D manages) and the dashboard's expanded
 * session row (read-only). The server decides who may change what;
 * `canManage` only shows the controls.
 *
 * Downloads are plain anchors on the authenticated GET — PDFs, images and
 * videos open in the browser, office files download (utils/fileResponse.js);
 * a link row's anchor is the link itself.
 */

const KIND_META = {
  material: {
    accept: '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png',
    uploadPath: (id) => `/api/ld/sessions/${id}/files`,
    button: 'Upload Materials',
    hint: 'PDF, Word, PowerPoint, Excel or images, up to 50 MB each.',
    emptyManage: 'No materials yet — upload slides or handouts for the participants, or add a link.',
    emptyRead: 'No materials have been uploaded for this session.',
    icon: FileText,
    removeTitle: 'Remove material',
    linkHint: 'e.g. a deck on SharePoint',
  },
  recording: {
    accept: '.mp4,.m4v,.mov,.webm',
    uploadPath: (id) => `/api/ld/sessions/${id}/files/recordings`,
    button: 'Upload Video',
    hint: 'MP4, MOV or WebM up to 500 MB. For a larger video, add a link to where it is hosted.',
    emptyManage: 'No recording yet — upload the course video after the session, or add a link to it.',
    emptyRead: 'No recording has been added for this session.',
    icon: Video,
    removeTitle: 'Remove recording',
    linkHint: 'e.g. a video on Stream, Teams or a shared drive',
  },
};

export const MATERIAL_ACCEPT = KIND_META.material.accept;

const SessionMaterials = ({ sessionId, kind = 'material', files = [], canManage = false, onChanged, compact = false }) => {
  const meta = KIND_META[kind] || KIND_META.material;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null); // the row awaiting confirmation
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');

  const upload = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      picked.forEach((f) => fd.append('files', f));
      const data = await fetchJSON(meta.uploadPath(sessionId), { method: 'POST', body: fd });
      if (onChanged) onChanged(data.data || []);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const addLink = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/sessions/${sessionId}/files/links`, {
        method: 'POST', body: JSON.stringify({ kind, url: linkUrl.trim(), title: linkTitle.trim() }),
      });
      setLinkOpen(false);
      setLinkUrl('');
      setLinkTitle('');
      if (onChanged) onChanged(data.data || []);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const remove = async (file) => {
    const data = await fetchJSON(`/api/ld/sessions/${sessionId}/files/${file.id}`, { method: 'DELETE' });
    setRemoving(null);
    if (onChanged) onChanged(data.data || []);
  };

  const pad = compact ? 'px-3 py-2' : 'px-5 py-3';
  const Icon = meta.icon;

  return (
    <div>
      {files.length === 0 ? (
        <p className={`text-sm text-slate-500 ${compact ? 'px-3 py-3' : 'px-5 py-6'} text-center`}>
          {canManage ? meta.emptyManage : meta.emptyRead}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {files.map((f) => (
            <li key={f.id} className={`flex items-center gap-3 ${pad}`}>
              {f.url ? <Link2 className="w-4 h-4 text-slate-400 shrink-0" /> : <Icon className="w-4 h-4 text-slate-400 shrink-0" />}
              <div className="min-w-0 flex-1">
                <a
                  href={f.url || `${API_BASE_URL}/api/ld/sessions/${sessionId}/files/${f.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 break-all"
                >
                  {f.originalName}
                  {f.url && <ExternalLink className="w-3.5 h-3.5 shrink-0 text-slate-400" />}
                </a>
                <p className="text-xs text-slate-500 mt-0.5 break-all">
                  {f.url ? f.url : fmtBytes(f.sizeBytes)}
                  {f.uploadedBy ? ` · ${f.uploadedBy}` : ''}
                  {f.createdAt ? ` · ${formatDateTime(f.createdAt)}` : ''}
                </p>
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => setRemoving(f)}
                  disabled={busy}
                  title="Remove"
                  className="shrink-0 p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className={`border-t border-slate-100 ${pad} space-y-2`}>
          <div className="flex flex-wrap items-center gap-3">
            <FilePicker
              multiple
              accept={meta.accept}
              disabled={busy}
              onChange={upload}
              showSelection={false}
              buttonLabel={busy ? 'Working…' : meta.button}
            />
            <button
              type="button"
              onClick={() => setLinkOpen((v) => !v)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <Link2 className="w-4 h-4" /> Add Link
            </button>
            <span className="text-xs text-slate-500">{meta.hint}</span>
            {busy && <Upload className="w-4 h-4 text-slate-400 animate-pulse" />}
          </div>
          {linkOpen && (
            <div className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <label className="flex-1 min-w-[220px] text-xs text-slate-600">
                Link
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className={`${inputCls} mt-1`}
                  placeholder="https://…"
                  maxLength={2000}
                />
              </label>
              <label className="flex-1 min-w-[180px] text-xs text-slate-600">
                Title
                <input
                  type="text"
                  value={linkTitle}
                  onChange={(e) => setLinkTitle(e.target.value)}
                  className={`${inputCls} mt-1`}
                  placeholder={meta.linkHint}
                  maxLength={255}
                />
              </label>
              <button
                type="button"
                onClick={addLink}
                disabled={busy || !linkUrl.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
              <button type="button" onClick={() => setLinkOpen(false)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className={`${pad} text-sm text-red-700 bg-red-50 border-t border-red-100`}>
          {error}
        </div>
      )}

      {removing && (
        <ConfirmDeleteModal
          title={meta.removeTitle}
          message={`Remove “${removing.originalName}” from this session? Participants will no longer see it. This cannot be undone.`}
          confirmLabel="Remove"
          onClose={() => setRemoving(null)}
          onConfirm={() => remove(removing)}
        />
      )}
    </div>
  );
};

export default SessionMaterials;
